import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Monitor, Smartphone } from 'lucide-react'
import type { Product, ProductCheckout } from '@entities/product/api/product.contract'
import { projectCheckoutForPreview } from '../lib/checkoutPreviewProjection'
import type { CheckoutSkin } from '../lib/checkoutStyle'
import {
  inlineRow,
  muted,
  previewPanel,
  previewToolbar,
  previewViewport,
} from './CheckoutBuilder.css'

export type PreviewDevice = 'desktop' | 'mobile'

export type CheckoutPreviewProps = {
  product: Product
  checkout: ProductCheckout
  skin: CheckoutSkin
  device: PreviewDevice
  onDeviceChange: (device: PreviewDevice) => void
}

const PREVIEW_URL = `${import.meta.env.VITE_CHECKOUT_WEB_URL ?? 'http://localhost:5174'}/preview`

const DEVICE_SIZE: Record<PreviewDevice, { width: number; height: number }> = {
  desktop: { width: 1060, height: 900 },
  mobile: { width: 390, height: 820 },
}

function DeviceButton({
  active,
  label,
  onClick,
  children,
}: {
  active: boolean
  label: string
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      style={{
        border: 0,
        borderRadius: 6,
        padding: '5px 9px',
        background: active ? 'rgba(4,147,102,.12)' : 'transparent',
        color: active ? '#049366' : '#9AA2B1',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  )
}

export function CheckoutPreview({
  product,
  checkout,
  skin,
  device,
  onDeviceChange,
}: CheckoutPreviewProps) {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const projection = useMemo(
    () => projectCheckoutForPreview(product, checkout, skin),
    [product, checkout, skin],
  )
  const size = DEVICE_SIZE[device]

  const postProjection = useCallback(() => {
    frameRef.current?.contentWindow?.postMessage({ type: 'vp-preview', checkout: projection }, '*')
  }, [projection])

  useEffect(() => {
    postProjection()
  }, [postProjection])

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if ((event.data as { type?: string } | null)?.type === 'vp-preview-ready') {
        postProjection()
      }
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [postProjection])

  useEffect(() => {
    const el = viewportRef.current
    if (!el) return
    function update() {
      const avail = el!.clientWidth - 48
      setScale(Math.min(1, Math.max(0.2, avail / size.width)))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => observer.disconnect()
  }, [size.width])

  return (
    <div className={previewPanel}>
      <div className={previewToolbar}>
        <span className={muted}>Pré-visualização ao vivo</span>
        <div className={inlineRow}>
          <DeviceButton
            active={device === 'desktop'}
            label="Preview desktop"
            onClick={() => onDeviceChange('desktop')}
          >
            <Monitor size={14} />
          </DeviceButton>
          <DeviceButton
            active={device === 'mobile'}
            label="Preview mobile"
            onClick={() => onDeviceChange('mobile')}
          >
            <Smartphone size={14} />
          </DeviceButton>
        </div>
      </div>
      <div className={previewViewport} ref={viewportRef}>
        <div style={{ width: size.width * scale, height: size.height * scale, overflow: 'hidden' }}>
          <iframe
            ref={frameRef}
            title="Pré-visualização do checkout"
            src={PREVIEW_URL}
            onLoad={postProjection}
            style={{
              width: size.width,
              height: size.height,
              border: 0,
              borderRadius: 14,
              display: 'block',
              background: '#f5f6f8',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
            }}
          />
        </div>
      </div>
    </div>
  )
}
